import React, { useState, useEffect, useCallback } from 'react';
import {
  Wand2,
  Upload,
  Trash2,
  RefreshCw,
  Server,
  Activity,
  CheckCircle2,
  AlertCircle,
  Settings,
  Save,
  X
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { toast } from '../components/CustomToast';

const apiUrl = process.env.REACT_APP_BACKEND_URL;

export default function InstantCloneDashboard() {
  const { token } = useAuth();
  const [voices, setVoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [gpuHealth, setGpuHealth] = useState(null);
  // The GPU server address is admin-editable at runtime: the box is a rented
  // instance whose address changes, and every agent worker reads this value,
  // so moving it must not require redeploying core or the workers.
  const [omniSettings, setOmniSettings] = useState(null);
  const [showServerSettings, setShowServerSettings] = useState(false);
  const [urlDraft, setUrlDraft] = useState('');
  const [savingUrl, setSavingUrl] = useState(false);
  
  // Create form state
  const [isCreating, setIsCreating] = useState(false);
  const [formData, setFormData] = useState({
    voice_id: '',
    language: 'hi',
    transcript: '',
  });
  const [audioFile, setAudioFile] = useState(null);

  const fetchVoices = useCallback(async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${apiUrl}/api/v1/admin/instant-clone/voices`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVoices(response.data?.voices || []);
    } catch (err) {
      toast.error('Failed to load instant clone voices');
    } finally {
      setLoading(false);
    }
  }, [token]);

  const checkGpuHealth = useCallback(async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${apiUrl}/api/v1/admin/instant-clone/gpu-health`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGpuHealth(response.data);
    } catch (err) {
      setGpuHealth({ status: 'unreachable', detail: 'Server unreachable' });
    }
  }, [token]);

  const fetchOmniSettings = useCallback(async () => {
    if (!token) return;
    try {
      // probe=false: the badge above already health-checks, and this call runs
      // on every page load.
      const response = await axios.get(
        `${apiUrl}/api/v1/admin/omnivoice/settings?probe=false`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setOmniSettings(response.data);
      setUrlDraft(response.data?.gpu_url || '');
    } catch (err) {
      // Non-fatal: the rest of the page still works off the env-configured URL.
      setOmniSettings(null);
    }
  }, [token]);

  const saveGpuUrl = async (force = false) => {
    if (!token) return;
    setSavingUrl(true);
    try {
      const response = await axios.put(
        `${apiUrl}/api/v1/admin/omnivoice/settings`,
        { gpu_url: urlDraft, force },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setOmniSettings(response.data);
      setUrlDraft(response.data?.gpu_url || '');
      setShowServerSettings(false);
      toast.success('GPU server URL updated. Workers pick it up within seconds.');
      checkGpuHealth();
      fetchVoices();
    } catch (err) {
      // The backend refuses an unreachable URL unless forced, and its message
      // says so -- surface it verbatim rather than inventing a summary.
      toast.error(err?.response?.data?.detail || 'Failed to update GPU server URL');
    } finally {
      setSavingUrl(false);
    }
  };

  useEffect(() => {
    fetchVoices();
    checkGpuHealth();
    fetchOmniSettings();
  }, [fetchVoices, checkGpuHealth, fetchOmniSettings]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!audioFile) {
      toast.error('Please upload a reference audio file');
      return;
    }

    setIsCreating(true);
    const form = new FormData();
    Object.entries(formData).forEach(([k, v]) => form.append(k, v));
    form.append('audio_file', audioFile);

    try {
      await axios.post(`${apiUrl}/api/v1/admin/instant-clone/voices`, form, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      toast.success('Voice clone created successfully!');
      setFormData({
        voice_id: '',
        language: 'hi',
        transcript: '',
      });
      setAudioFile(null);
      fetchVoices();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Failed to create voice clone');
    } finally {
      setIsCreating(false);
    }
  };

  const handleDelete = async (id, voiceId) => {
    if (!window.confirm(`Are you sure you want to delete voice clone '${voiceId}'?`)) return;
    
    try {
      await axios.delete(`${apiUrl}/api/v1/admin/instant-clone/voices/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Voice deleted');
      fetchVoices();
    } catch (err) {
      toast.error('Failed to delete voice');
    }
  };

  return (
    <div className="flex-1 flex flex-col p-6">
      
      <main className="flex-1 min-h-0 overflow-y-auto  bg-gray-50 pl-14 lg:pl-0">
        
        {/* Header */}
        <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
          <div className="px-8 py-6 max-w-7xl mx-auto">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center border border-purple-200">
                  <Wand2 className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Voice Cloning</h1>
                  <p className="text-sm text-gray-500 mt-1">Manage OmniVoice zero-shot clone profiles. Test synthesis from Pronunciations.</p>
                </div>
              </div>

              {/* GPU Health Badge */}
              <div className="flex items-center bg-gray-50 rounded-lg p-3 border border-gray-200">
                <div className="flex items-center mr-4">
                  <Server className="w-5 h-5 text-gray-400 mr-2" />
                  <span className="text-sm font-medium text-gray-700">GPU Server</span>
                </div>
                {gpuHealth ? (
                  gpuHealth.status === 'ok' || gpuHealth.hybrid_optimized ? (
                    <div className="flex items-center text-green-600 bg-green-50 px-3 py-1 rounded-full text-xs font-semibold">
                      <CheckCircle2 className="w-4 h-4 mr-1" />
                      Online ({gpuHealth.device || 'CUDA'})
                    </div>
                  ) : (
                    <div className="flex items-center text-red-600 bg-red-50 px-3 py-1 rounded-full text-xs font-semibold">
                      <AlertCircle className="w-4 h-4 mr-1" />
                      {gpuHealth.detail || 'Error'}
                    </div>
                  )
                ) : (
                  <div className="flex items-center text-gray-500">
                    <Activity className="w-4 h-4 mr-1 animate-pulse" />
                    Checking...
                  </div>
                )}
                <button 
                  onClick={checkGpuHealth}
                  className="ml-3 text-gray-400 hover:text-gray-600 transition-colors"
                  title="Refresh GPU Health"
                >
                  <RefreshCw className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setShowServerSettings((open) => !open)}
                  className="ml-2 text-gray-400 hover:text-gray-600 transition-colors"
                  title="Change GPU server URL"
                >
                  <Settings className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* GPU server address, editable at runtime.
                The OmniVoice box is a rented GPU instance whose address changes
                when it is restarted or migrated. Both this service and every
                agent worker read the stored value, so changing it here moves
                the whole fleet without a redeploy. */}
            {showServerSettings && (
              <div className="mt-4 bg-gray-50 border border-gray-200 rounded-xl p-4">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900">GPU server URL</h3>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Applies to this dashboard and to every agent worker, within a few seconds. No redeploy needed.
                    </p>
                  </div>
                  <button
                    onClick={() => setShowServerSettings(false)}
                    className="text-gray-400 hover:text-gray-600"
                    title="Close"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>

                <div className="flex flex-col sm:flex-row gap-2">
                  <input
                    type="url"
                    value={urlDraft}
                    onChange={(e) => setUrlDraft(e.target.value)}
                    placeholder="http://1.2.3.4:6006"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                  <button
                    onClick={() => saveGpuUrl(false)}
                    disabled={savingUrl || !urlDraft.trim()}
                    className="inline-flex items-center justify-center px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-semibold hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Save className="w-4 h-4 mr-1.5" />
                    {savingUrl ? 'Saving...' : 'Save'}
                  </button>
                  {/* The save is rejected unless the new address answers
                      /healthz, since a bad value breaks TTS fleet-wide. This is
                      the deliberate override for a box that is still booting. */}
                  <button
                    onClick={() => saveGpuUrl(true)}
                    disabled={savingUrl || !urlDraft.trim()}
                    className="px-3 py-2 border border-gray-300 text-gray-600 rounded-lg text-sm font-medium hover:bg-gray-100 disabled:opacity-50"
                    title="Save without requiring the server to respond (use if it is still starting up)"
                  >
                    Save anyway
                  </button>
                </div>

                {omniSettings && (
                  <div className="mt-3 text-xs text-gray-500 space-y-1">
                    <div>
                      Current:{' '}
                      <span className="font-mono text-gray-700">{omniSettings.gpu_url}</span>{' '}
                      {omniSettings.source === 'env' ? (
                        <span className="text-amber-600">(from environment default &mdash; nothing saved yet)</span>
                      ) : (
                        <span className="text-gray-400">(saved)</span>
                      )}
                    </div>
                    {omniSettings.source === 'database' && (
                      <div>
                        Environment default:{' '}
                        <span className="font-mono">{omniSettings.env_gpu_url}</span>
                      </div>
                    )}
                    {omniSettings.updated_at && (
                      <div>Last changed {omniSettings.updated_at}</div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </header>

        {/* Scrollable Content */}
        <div className="p-8 max-w-7xl mx-auto space-y-8">
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Left Column: Create Form */}
              <div className="lg:col-span-1 space-y-8">
                <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
                  <div className="px-6 py-5 border-b border-gray-100 bg-gray-50/50">
                    <h2 className="text-lg font-semibold text-gray-900">Create Voice Clone</h2>
                  </div>
                  <div className="p-6">
                    <form onSubmit={handleCreate} className="space-y-5">
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Voice ID</label>
                        <input
                          type="text"
                          required
                          pattern="^[A-Za-z0-9_.-]+$"
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none transition-all text-sm"
                          placeholder="e.g., customer_001"
                          value={formData.voice_id}
                          onChange={(e) => setFormData({...formData, voice_id: e.target.value})}
                        />
                        <p className="text-xs text-gray-500 mt-1">Unique identifier (no spaces)</p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Language</label>
                        <select
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none text-sm bg-white"
                          value={formData.language}
                          onChange={(e) => setFormData({...formData, language: e.target.value})}
                        >
                          <option value="hi">Hindi (hi)</option>
                          <option value="en">English (en)</option>
                          <option value="te">Telugu (te)</option>
                          <option value="ta">Tamil (ta)</option>
                          <option value="mr">Marathi (mr)</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Reference Audio</label>
                        <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-lg hover:border-purple-400 transition-colors bg-gray-50">
                          <div className="space-y-1 text-center">
                            <Upload className="mx-auto h-8 w-8 text-gray-400" />
                            <div className="flex text-sm text-gray-600 justify-center">
                              <label htmlFor="audio-upload" className="relative cursor-pointer bg-transparent rounded-md font-medium text-purple-600 hover:text-purple-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-purple-500">
                                <span>Upload a file</span>
                                <input 
                                  id="audio-upload" 
                                  name="audio-upload" 
                                  type="file" 
                                  accept="audio/*" 
                                  className="sr-only" 
                                  onChange={(e) => setAudioFile(e.target.files[0])}
                                />
                              </label>
                            </div>
                            <p className="text-xs text-gray-500">
                              {audioFile ? audioFile.name : 'WAV, MP3 up to 10MB'}
                            </p>
                          </div>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Reference Transcript</label>
                        <textarea
                          required
                          rows="4"
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none transition-all text-sm resize-none"
                          placeholder="Exact text spoken in the audio file..."
                          value={formData.transcript}
                          onChange={(e) => setFormData({...formData, transcript: e.target.value})}
                        ></textarea>
                      </div>

                      <button
                        type="submit"
                        disabled={isCreating}
                        className="w-full py-2.5 px-4 bg-purple-600 hover:bg-purple-700 text-white font-medium rounded-lg transition-colors flex items-center justify-center disabled:opacity-70 mt-2"
                      >
                        {isCreating ? (
                          <RefreshCw className="w-5 h-5 animate-spin" />
                        ) : (
                          'Create Voice Clone'
                        )}
                      </button>
                    </form>
                  </div>
                </div>
              </div>

              {/* Right Column: Voices List */}
              <div className="lg:col-span-2 space-y-8">
                <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
                  <div className="px-6 py-5 border-b border-gray-100 bg-gray-50/50 flex justify-between items-center">
                    <h2 className="text-lg font-semibold text-gray-900">Cloned Voices</h2>
                    <span className="bg-gray-200 text-gray-700 text-xs font-semibold px-2.5 py-0.5 rounded-full">
                      {voices.length} voices
                    </span>
                  </div>
                  
                  {loading ? (
                    <div className="p-8 flex justify-center">
                      <RefreshCw className="w-6 h-6 animate-spin text-purple-500" />
                    </div>
                  ) : voices.length === 0 ? (
                    <div className="p-12 text-center">
                      <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Wand2 className="w-8 h-8 text-gray-400" />
                      </div>
                      <h3 className="text-gray-900 font-medium text-lg">No voices found</h3>
                      <p className="text-gray-500 mt-1">Create your first instant clone using the form on the left.</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Voice ID</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Lang</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">GPU Status</th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {voices.map((v) => (
                            <tr key={v.id} className="hover:bg-gray-50 transition-colors">
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm font-semibold text-gray-900">{v.voice_id}</div>
                                <div className="text-xs text-gray-500 mt-0.5" title={v.transcript}>
                                  {v.transcript.length > 30 ? v.transcript.substring(0, 30) + '...' : v.transcript}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded-md text-xs font-medium">
                                  {v.language.toUpperCase()}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                {v.gpu_registered ? (
                                  <div className="flex items-center text-xs text-green-600 font-medium">
                                    <CheckCircle2 className="w-3.5 h-3.5 mr-1" />
                                    Ready ({v.prompt_tokens} tk)
                                  </div>
                                ) : (
                                  <div className="flex items-center text-xs text-yellow-600 font-medium">
                                    <AlertCircle className="w-3.5 h-3.5 mr-1" />
                                    Not on GPU
                                  </div>
                                )}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                <button
                                  onClick={() => handleDelete(v.id, v.voice_id)}
                                  className="text-red-500 hover:text-red-700"
                                >
                                  <Trash2 className="w-4 h-4 inline" />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
          </div>

        </div>
      </main>
    </div>
  );
}
